"use client"

import { useMemo, useState } from "react";
import { Clipboard, Download } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { copyToClipboard } from "@/lib/utils";
import { useTranslations } from "@/components/i18n/use-translations";

const isValidIPv6 = (value: string) => {
    const normalized = value.trim();

    if (!normalized || !/^[0-9A-Fa-f:]+$/.test(normalized)) return false;
    if ((normalized.match(/::/g) || []).length > 1) return false;

    const [left, right] = normalized.split("::");
    const leftParts = left ? left.split(":").filter(Boolean) : [];
    const rightParts = right ? right.split(":").filter(Boolean) : [];

    if ([...leftParts, ...rightParts].some((segment) => segment.length > 4)) return false;

    const totalSegments = leftParts.length + rightParts.length;
    const hasCompression = normalized.includes("::");

    if (hasCompression) {
        return totalSegments < 8 && totalSegments >= 1;
    }

    return totalSegments === 8;
};

// Restore common defang notations so defanged reports extract cleanly
const refang = (text: string) =>
    text
        .replace(/\bhxxp(s?)/gi, "http$1")
        .replace(/\[(?:\.|dot)\]|\((?:\.|dot)\)|\{(?:\.|dot)\}/gi, ".")
        .replace(/\[:\]|\[:\/\/\]/g, (match) => match.slice(1, -1))
        .replace(/\[(?:@|at)\]|\((?:@|at)\)/gi, "@");

const extractIocs = (text: string) => {
    const normalized = refang(text || "");
    const results = {
        urls: new Set<string>(),
        domains: new Set<string>(),
        emails: new Set<string>(),
        ipv4: new Set<string>(),
        ipv6: new Set<string>(),
        md5: new Set<string>(),
        sha1: new Set<string>(),
        sha256: new Set<string>(),
        cves: new Set<string>(),
    };

    const urlRegex = /https?:\/\/[^\s"'<>]+/gi;
    const domainRegex = /\b(?:(?!-)[A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,}\b/g;
    const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
    const ipv4Regex = /\b(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\b/g;
    const ipv6CandidateRegex = /\b[0-9A-Fa-f]{1,4}(?::[0-9A-Fa-f]{0,4}){2,7}\b/g;
    const md5Regex = /\b[a-fA-F0-9]{32}\b/g;
    const sha1Regex = /\b[a-fA-F0-9]{40}\b/g;
    const sha256Regex = /\b[a-fA-F0-9]{64}\b/g;
    const cveRegex = /\bCVE-\d{4}-\d{4,7}\b/gi;

    normalized.match(urlRegex)?.forEach((item) => results.urls.add(item.replace(/[\[\]]/g, "")));
    normalized.match(domainRegex)?.forEach((item) => results.domains.add(item.replace(/[\[\]]/g, "")));
    normalized.match(emailRegex)?.forEach((item) => results.emails.add(item.replace(/[\[\]]/g, "")));
    normalized.match(ipv4Regex)?.forEach((item) => results.ipv4.add(item.replace(/[\[\]]/g, "")));
    normalized.match(ipv6CandidateRegex)?.forEach((item) => {
        const cleaned = item.replace(/[\[\]]/g, "");
        if (isValidIPv6(cleaned)) {
            results.ipv6.add(cleaned);
        }
    });
    normalized.match(md5Regex)?.forEach((item) => results.md5.add(item.toLowerCase()));
    normalized.match(sha1Regex)?.forEach((item) => results.sha1.add(item.toLowerCase()));
    normalized.match(sha256Regex)?.forEach((item) => results.sha256.add(item.toLowerCase()));
    normalized.match(cveRegex)?.forEach((item) => results.cves.add(item.toUpperCase()));

    return {
        urls: Array.from(results.urls),
        domains: Array.from(results.domains),
        emails: Array.from(results.emails),
        ipv4: Array.from(results.ipv4),
        ipv6: Array.from(results.ipv6),
        md5: Array.from(results.md5),
        sha1: Array.from(results.sha1),
        sha256: Array.from(results.sha256),
        cves: Array.from(results.cves),
    };
};

const GROUPS = [
    { key: "urls", title: "URLs" },
    { key: "domains", title: "Domains" },
    { key: "emails", title: "Emails" },
    { key: "ipv4", title: "IPv4" },
    { key: "ipv6", title: "IPv6" },
    { key: "sha256", title: "SHA-256" },
    { key: "sha1", title: "SHA-1" },
    { key: "md5", title: "MD5" },
    { key: "cves", title: "CVEs" },
] as const;

const downloadFile = (content: string, fileName: string, type: string) => {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
};

export function IocExtractor() {
    const [text, setText] = useState("");
    const [fileName, setFileName] = useState<string | null>(null);
    const { messages } = useTranslations();

    const extracted = useMemo(() => extractIocs(text), [text]);
    const total = GROUPS.reduce((sum, group) => sum + extracted[group.key].length, 0);

    const exportJson = () => {
        downloadFile(JSON.stringify(extracted, null, 2), "iocs.json", "application/json");
    };

    const exportCsv = () => {
        const rows = GROUPS.flatMap((group) => extracted[group.key].map((item) => `${group.key},"${item.replace(/"/g, '""')}"`));
        downloadFile(["type,value", ...rows].join("\n"), "iocs.csv", "text/csv");
    };

    const handleFile = (file?: File) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const content = typeof reader.result === "string" ? reader.result : "";
            setText(content);
            setFileName(file.name);
        };
        reader.readAsText(file);
    };

    return (
        <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
            <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/70 p-5 shadow-lg shadow-cyan-500/10">
                <div className="flex flex-col gap-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">{messages.tools.iocExtractor.label}</p>
                    <h1 className="text-2xl font-semibold text-white">{messages.tools.iocExtractor.title}</h1>
                    <p className="text-sm text-gray-400">{messages.tools.iocExtractor.description}</p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-gray-400">{fileName ? <span className="text-gray-300">{fileName}</span> : null}</div>
                    <div className="flex flex-wrap items-center gap-2">
                        {total > 0 && (
                            <>
                                <button
                                    type="button"
                                    onClick={exportJson}
                                    className="inline-flex h-10 items-center gap-2 rounded-full border border-slate-700 px-4 text-sm text-gray-200 transition-colors hover:border-cyan-500/60 hover:text-cyan-200"
                                >
                                    <Download className="h-4 w-4" />
                                    {messages.tools.iocExtractor.exportJson}
                                </button>
                                <button
                                    type="button"
                                    onClick={exportCsv}
                                    className="inline-flex h-10 items-center gap-2 rounded-full border border-slate-700 px-4 text-sm text-gray-200 transition-colors hover:border-cyan-500/60 hover:text-cyan-200"
                                >
                                    <Download className="h-4 w-4" />
                                    {messages.tools.iocExtractor.exportCsv}
                                </button>
                            </>
                        )}
                        <label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-full bg-linear-to-r from-cyan-500 to-blue-600 px-4 text-sm font-semibold text-white shadow-md shadow-cyan-500/25 transition hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/35">
                            <Input
                                type="file"
                                className="hidden"
                                onChange={(event) => handleFile(event.target.files?.[0] ?? undefined)}
                            />
                            <span>{messages.home.uploadLabel}</span>
                        </label>
                    </div>
                </div>

                <div className="relative">
                    <Textarea
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                        placeholder={messages.tools.iocExtractor.placeholder}
                        aria-label={messages.tools.iocExtractor.placeholder}
                        className="min-h-[200px] border border-slate-800 bg-transparent text-slate-100 placeholder:text-gray-500 pr-14 ring-2 ring-cyan-500/20 focus:ring-cyan-500/45"
                    />
                    <button
                        type="button"
                        onClick={() => copyToClipboard(text)}
                        className="group absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-lg p-2 text-gray-300 transition-colors hover:bg-slate-700/40 hover:text-cyan-400"
                        aria-label={messages.tools.iocExtractor.copyAll}
                    >
                        <Clipboard className="h-5 w-5 text-current transition-colors group-hover:text-cyan-400" />
                    </button>
                </div>
                <p className="text-xs text-gray-500">{messages.tools.iocExtractor.refangNote}</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {GROUPS.map((group) => (
                    <IocGroup
                        key={group.key}
                        title={group.title}
                        items={extracted[group.key]}
                        empty={messages.tools.iocExtractor.emptyResult}
                        countLabel={messages.tools.iocExtractor.resultLabel}
                        copyLabel={messages.tools.iocExtractor.copyAll}
                    />
                ))}
            </div>
        </div>
    );
}

function IocGroup({
    title,
    items,
    empty,
    countLabel,
    copyLabel,
}: {
    title: string;
    items: string[];
    empty: string;
    countLabel: string;
    copyLabel: string;
}) {
    return (
        <div className="rounded-xl border border-slate-800/80 bg-slate-950/60 p-4 space-y-2">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">{title}</h3>
                <span className="text-xs text-gray-400">{items.length} {countLabel}</span>
            </div>
            {items.length ? (
                <ul className="space-y-1 max-h-48 overflow-auto pr-1">
                    {items.map((item) => (
                        <li
                            key={item}
                            className="text-sm text-gray-200 break-all rounded-lg bg-slate-900/60 border border-slate-800/70 px-3 py-2 flex items-center justify-between gap-3"
                        >
                            <span className="break-all">{item}</span>
                            <button
                                type="button"
                                onClick={() => copyToClipboard(item)}
                                className="group inline-flex items-center justify-center rounded-lg p-2 text-gray-300 transition-colors hover:bg-slate-700/40 hover:text-cyan-400"
                                aria-label={`${copyLabel} ${title}`}
                            >
                                <Clipboard className="h-5 w-5 text-current transition-colors group-hover:text-cyan-400" />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-sm text-gray-500">{empty}</p>
            )}
        </div>
    );
}
